import React from "react";
import { Doughnut } from "react-chartjs-2";
import { Chart as ChartJS, ArcElement, Tooltip, Legend, Title } from "chart.js";
import ChartDataLabels from "chartjs-plugin-datalabels";
import { Box, Typography, Paper } from "@mui/material";
import {
  formatCurrency,
  formatCurrencyCompact,
  formatHandlerCode,
} from "../../../utils/namesFormater";
import { MimshakGroupByService } from "../../../services/MimshakGroupByService";
import {
  LiquidityPeriodBalanceType,
  Product,
} from "../../../interfaces/DTO/mislaka_xml_models";
import { formatLiquidityPeriodBalanceType } from "../../../utils/enumFormatter";
import { MimshakService } from "../../../services/mimshakService";
import { colorHandlerCode } from "../../../utils/colorEnumFormatter";

ChartJS.register(ArcElement, Tooltip, Legend, Title, ChartDataLabels);

interface GroupByHandlerPieChartProps {
  products: Product[];
  balanceType?: LiquidityPeriodBalanceType;
  showLegend?: boolean;
}

export const GroupByHandlerPieChart: React.FC<GroupByHandlerPieChartProps> = ({
  products,
  balanceType,
  showLegend = true,
}) => {
  const groupedData = MimshakGroupByService.groupByHandlerCode(
    products,
    balanceType
  );
  const total = balanceType
    ? MimshakService.calculateTotalFundSum(products, balanceType)
    : MimshakService.calculateTotalsFromProducts(products);

    if (!groupedData || Object.keys(groupedData).length === 0) return null;
  const handlerCodes = Object.keys(groupedData);
  const labels = handlerCodes.map((handlerCode) =>
    formatHandlerCode(handlerCode)
  );
  const dataValues = Object.values(groupedData);

  const backgroundColors = handlerCodes.map(colorHandlerCode);

  const data = {
    labels,
    datasets: [
      {
        data: dataValues,
        backgroundColor: backgroundColors,
        hoverBackgroundColor: backgroundColors,
      },
    ],
  };

  const options = {
    responsive: true,
    maintainAspectRatio: true,
    layout: {
      padding: 6,
    },
    plugins: {
      legend: {
        display: showLegend,
        position: "top" as const,
      },
      datalabels: {
        color: (context: any) => {
          const backgroundColor =
            context.dataset.backgroundColor[context.dataIndex];
          const isDark = backgroundColor && isColorDark(backgroundColor);
          return isDark ? "#fff" : "#000";
        },
        font: {
          weight: "bold" as
            | "bold"
            | "normal"
            | "bolder"
            | "lighter"
            | undefined,
          size: 14,
        },
        formatter: (value: number) => {
          return formatCurrencyCompact(value);
        },
        clip: true,
      },
    },
  };

  const isColorDark = (color: string): boolean => {
    if (color.startsWith("hsl")) {
      const match = color.match(/hsl\\((\\d+),\\s*(\\d+)%?,\\s*(\\d+)%?\\)/);
      if (match) {
        const l = parseInt(match[3], 10);
        return l < 50;
      }
    }
    const rgb = color
      .replace(/^rgba?\\(|\\s+|\\)$/g, "")
      .split(",")
      .map(Number);
    const [r, g, b] = rgb;
    const brightness = (r * 299 + g * 587 + b * 114) / 1000;
    return brightness < 128;
  };

  return (
    <Paper elevation={3} sx={{ padding: 3 }}>
      <Box display="flex" alignItems="baseline" gap={1}>
        <Typography variant="body1" gutterBottom>
          לפי יצרן |
        </Typography>
        <Typography variant="h6" gutterBottom>
          {balanceType ? formatLiquidityPeriodBalanceType(balanceType) : "סה\"כ"} : {formatCurrency(total)}
        </Typography>
      </Box>
      <Box
        sx={{ display: "flex", justifyContent: "center", alignItems: "center" }}
      >
        <Box sx={{ width: "100%" }}>
          <Doughnut data={data} options={options} />
        </Box>
      </Box>
    </Paper>
  );
};
